import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import vertexShader from "./shaders/waterWaves/vertex.glsl";
import fragmentShader from "./shaders/waterWaves/fragment.glsl";
import GUI from "lil-gui";

/**
 * Base
 */
// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * GUI
 */
const gui = new GUI();
const debugObject = {
  uBigWavesFrequency: new THREE.Vector2(2.32, 0.842),
  uBigWavesSpeed: new THREE.Vector2(0.75, 0.75),
  uBigWavesElevation: 0.2,
  uSmallWavesIterations: 4,
  uSmallWavesFrequency: 3,
  uSmallWavesSpeed: 0.2,
  uSmallWavesElevation: 0.15,
  uTopColor: "#abd7e2",
  uBottomColor: "#579ebc",
  uColorOffset: 0.08,
  uColorMultiplier: 3.372,
};

/**
 * Water
 *
 * Your scene goes here.
 */

const waterPlaneGeometry = new THREE.PlaneGeometry(4, 4, 512, 512);
const waterPlaneMaterial = new THREE.ShaderMaterial({
  vertexShader: vertexShader,
  fragmentShader: fragmentShader,
  uniforms: {
    uTime: new THREE.Uniform(0),
    uBigWavesElevation: new THREE.Uniform(debugObject.uBigWavesElevation),
    uBigWavesFrequency: new THREE.Uniform(
      new THREE.Vector2(
        debugObject.uBigWavesFrequency.x,
        debugObject.uBigWavesFrequency.y,
      ),
    ),
    uBigWavesSpeed: new THREE.Uniform(
      new THREE.Vector2(
        debugObject.uBigWavesSpeed.x,
        debugObject.uBigWavesSpeed.y,
      ),
    ),
    uTopColor: new THREE.Uniform(new THREE.Color(debugObject.uTopColor)),
    uBottomColor: new THREE.Uniform(new THREE.Color(debugObject.uBottomColor)),
    uColorOffset: new THREE.Uniform(debugObject.uColorOffset),
    uColorMultiplier: new THREE.Uniform(debugObject.uColorMultiplier),
    uSmallWavesIterations: new THREE.Uniform(debugObject.uSmallWavesIterations),
    uSmallWavesFrequency: new THREE.Uniform(debugObject.uSmallWavesFrequency),
    uSmallWavesSpeed: new THREE.Uniform(debugObject.uSmallWavesSpeed),
    uSmallWavesElevation: new THREE.Uniform(debugObject.uSmallWavesElevation),
  },
});

gui
  .add(waterPlaneMaterial.uniforms.uBigWavesElevation, "value")
  .min(0)
  .max(0.2)
  .step(0.001)
  .name("Big Waves Elevation");
gui
  .add(waterPlaneMaterial.uniforms.uBigWavesFrequency.value, "x")
  .min(0)
  .max(10)
  .step(0.001)
  .name("Big Waves Frequency X");
gui
  .add(waterPlaneMaterial.uniforms.uBigWavesFrequency.value, "y")
  .min(0)
  .max(10)
  .step(0.001)
  .name("Big Waves Frequency Y");
gui
  .add(waterPlaneMaterial.uniforms.uBigWavesSpeed.value, "x")
  .min(0)
  .max(10)
  .step(0.001)
  .name("Big Waves Speed X");
gui
  .add(waterPlaneMaterial.uniforms.uBigWavesSpeed.value, "y")
  .min(0)
  .max(10)
  .step(0.001)
  .name("Big Waves Speed Y");
  gui
  .add(waterPlaneMaterial.uniforms.uSmallWavesIterations, "value")
  .min(0)
  .max(10)
  .step(1)
  .name("Small Waves Iterations");
gui
  .add(waterPlaneMaterial.uniforms.uSmallWavesFrequency, "value")
  .min(0)
  .max(10)
  .step(0.001)
  .name("Small Waves Frequency");
gui
  .add(waterPlaneMaterial.uniforms.uSmallWavesSpeed, "value")
  .min(0)
  .max(10)
  .step(0.001)
  .name("Small Waves Speed");
gui
  .add(waterPlaneMaterial.uniforms.uSmallWavesElevation, "value")
  .min(0)
  .max(1)
  .step(0.001)
  .name("Small Waves Elevation");
gui
  .addColor(debugObject, "uTopColor")
  .onChange(() => {
    waterPlaneMaterial.uniforms.uTopColor.value.set(debugObject.uTopColor);
  })
  .name("Top Color")
gui
  .addColor(debugObject, "uBottomColor")
  .onChange(() => {
    waterPlaneMaterial.uniforms.uBottomColor.value.set(
      debugObject.uBottomColor,
    );
  })
  .name("Bottom Color")
gui
  .add(waterPlaneMaterial.uniforms.uColorOffset, "value")
  .min(0)
  .max(1)
  .step(0.001)
  .name("Color Offset");
gui
  .add(waterPlaneMaterial.uniforms.uColorMultiplier, "value")
  .min(0)
  .max(1)
  .step(0.001)
  .name("Color Multiplier");

const waterPlane = new THREE.Mesh(waterPlaneGeometry, waterPlaneMaterial);
waterPlane.rotation.x = -Math.PI * 0.5;
scene.add(waterPlane);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100,
);
camera.position.set(1, 1, 1);
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();

  waterPlaneMaterial.uniforms.uTime.value = elapsedTime;

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
